import { RiPushpinFill, RiPushpinLine } from "@remixicon/react";
import { Button } from "@screener/uikit/components/button";
import { cn } from "@screener/uikit/lib/utils";
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react";
import { pinButtonLabel, type RowPinMatch } from "./pin-match";

/**
 * Кнопка закрепления уровня §6.7 — один компонент экрана для строки таблицы
 * и заголовка детальной панели (review блок C, по образцу
 * `FavoriteStarButton`): обе закрепляют один и тот же уровень одним и тем же
 * запросом, и разметка не имеет права разойтись копией.
 */
export interface PinToggleButtonProps {
  /** Закрепление §6.7, сопоставленное со строкой. `"unknown"` — список ещё не прочитан или недоступен. */
  pinMatch: RowPinMatch | "unknown" | undefined;
  /** Свой запрос закрепления этой строки ещё не завершился — кнопка недоступна. */
  pending: boolean;
  /** Закрепить уровень строки или снять уже стоящее закрепление — одно действие. */
  onToggle: () => void;
  /**
   * Строка таблицы исключает кнопку из роверного `tabindex`: доступ к
   * действию с клавиатуры — хоткеем `p` сфокусированной строки, а не вторым
   * проходом табом. Панель такого не делает — там кнопка входит в обычный
   * табовый проход.
   */
  tabIndex?: number;
  /**
   * Строка гасит активацию кнопки мышью/`Enter`/`Space`, чтобы клик по
   * значку не открывал панель заодно (`stopButtonActivation`). Панель
   * передаёт действие напрямую.
   */
  stopActivation?: (event: ReactMouseEvent | ReactKeyboardEvent) => void;
  /** Довесок к позиционированию кнопки в разметке вызывающей стороны. */
  className?: string;
}

export function PinToggleButton({
  pinMatch,
  pending,
  onToggle,
  tabIndex,
  stopActivation,
  className,
}: PinToggleButtonProps) {
  const pinned = pinMatch !== "unknown" && pinMatch !== undefined;
  const label = pinButtonLabel(pinMatch);

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      tabIndex={tabIndex}
      disabled={pending}
      aria-pressed={pinMatch === "unknown" ? "mixed" : pinned}
      aria-label={label}
      className={cn("size-6", pinned ? "text-pin" : "text-muted-foreground", className)}
      onClick={(event) => {
        stopActivation?.(event);
        onToggle();
      }}
      onKeyDown={stopActivation}
    >
      {pinned ? <RiPushpinFill className="size-3.5" /> : <RiPushpinLine className="size-3.5" />}
    </Button>
  );
}
